import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-error';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { organizationCodeRule, emailRule, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField } from '@/lib/form-validation';

const schema = z.object({
  organizationCode: organizationCodeRule,
  email: emailRule,
});

type FormData = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { organizationCode: data.organizationCode, email: data.email });
      setSent(true);
      addToast('Reset link sent to your email', 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed to send reset link', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-700 to-brand-900 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Mail className="text-emerald-600" size={24} />
            </div>
            <CardTitle>Check Your Email</CardTitle>
            <CardDescription>If an account exists with that email, we've sent a password reset link.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button variant="outline" className="w-full">Back to Sign In</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-700 to-brand-900 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Forgot Password</CardTitle>
          <CardDescription>Enter your Organization ID and email to receive a reset link</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
            <div>
              <Label htmlFor="organizationCode">Organization ID</Label>
              <Input
                id="organizationCode"
                type="text"
                placeholder="SWAYAM001"
                autoComplete="organization"
                {...register('organizationCode', { setValueAs: (v: string) => v.trim().toUpperCase() })}
                aria-invalid={errors.organizationCode ? true : undefined}
                aria-describedby={errors.organizationCode ? 'organizationCode-error' : undefined}
                className={cn(errors.organizationCode && 'border-error')}
              />
              <FormError id="organizationCode-error" message={errors.organizationCode?.message} />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                {...register('email')}
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className={cn(errors.email && 'border-error')}
              />
              <FormError id="email-error" message={errors.email?.message} />
            </div>
            <Button type="submit" className="w-full" loading={loading}>Send Reset Link</Button>
            <Link to="/login">
              <Button variant="ghost" className="w-full">Back to Sign In</Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
